import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Fade, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import Iconify from 'components/iconify';
import { useTutorial, TOTAL_STEPS } from 'routes/tutorial-context';
import { paths } from 'routes/paths';
import { fillRoute } from 'routes/hooks';

// --- Helpers -------------------------------------------------------------------

const PADDING = 10;

function getTargetRect(targetId) {
  if (!targetId) return null;
  const el = document.querySelector(`[data-tutorial-id="${targetId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom };
}

// --- Spotlight overlay ---------------------------------------------------------
// Single SVG with a mask for rounded-corner cutout (nice aesthetics).

function SpotlightOverlay({ rect, interactive, onTapOutside }) {
  const color = 'rgba(0,0,0,0.65)';
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    return (
      <svg
        onClick={onTapOutside}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 1400,
          pointerEvents: interactive ? 'none' : 'all',
        }}
        viewBox={`0 0 ${vw} ${vh}`}
        preserveAspectRatio="none"
      >
        <rect width={vw} height={vh} fill={color} />
      </svg>
    );
  }

  const x = Math.max(0, rect.left - PADDING);
  const y = Math.max(0, rect.top - PADDING);
  const w = rect.width + PADDING * 2;
  const h = rect.height + PADDING * 2;
  const r = 12;

  return (
    <svg
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 1400,
        pointerEvents: 'none',
      }}
      viewBox={`0 0 ${vw} ${vh}`}
      preserveAspectRatio="none"
    >
      <defs>
        <mask id="spotlight-mask">
          <rect width={vw} height={vh} fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="black" />
        </mask>
      </defs>

      {/* Dimmed background with rounded cutout */}
      <rect width={vw} height={vh} fill={color} mask="url(#spotlight-mask)" />

      {/* White border around the cutout */}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={r}
        ry={r}
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        opacity="0.6"
        style={{ pointerEvents: 'none' }}
      />

      {/* 4 invisible tap-blocking rects outside the cutout.
          Only rendered for non-interactive steps. The cutout gap between them
          is left empty so taps there reach the real element below. */}
      {!interactive && (
        <>
          {/* TOP */}
          <rect
            x={0}
            y={0}
            width={vw}
            height={y}
            fill="transparent"
            style={{ pointerEvents: 'all', cursor: 'default' }}
            onClick={onTapOutside}
          />
          {/* BOTTOM */}
          <rect
            x={0}
            y={y + h}
            width={vw}
            height={Math.max(0, vh - y - h)}
            fill="transparent"
            style={{ pointerEvents: 'all', cursor: 'default' }}
            onClick={onTapOutside}
          />
          {/* LEFT */}
          <rect
            x={0}
            y={y}
            width={x}
            height={h}
            fill="transparent"
            style={{ pointerEvents: 'all', cursor: 'default' }}
            onClick={onTapOutside}
          />
          {/* RIGHT */}
          <rect
            x={x + w}
            y={y}
            width={Math.max(0, vw - x - w)}
            height={h}
            fill="transparent"
            style={{ pointerEvents: 'all', cursor: 'default' }}
            onClick={onTapOutside}
          />
        </>
      )}
    </svg>
  );
}

// --- Tooltip card --------------------------------------------------------------

function TooltipCard({ step, stepIndex, total, onNext, onSkip, targetRect }) {
  const { i18n } = useLingui();
  const isCenter = step.position === 'center' || !targetRect;
  const isBranding = step.id === 'branding-upload';

  let tooltipStyle = {};
  if (!isCenter && targetRect) {
    const vw = window.innerWidth;
    const cardWidth = Math.min(vw - 32, 360);
    const leftPos = Math.max(16, Math.min(targetRect.left, vw - cardWidth - 16));
    if (step.position === 'above') {
      tooltipStyle = {
        bottom: window.innerHeight - targetRect.top + PADDING + 12,
        left: leftPos,
        width: cardWidth,
      };
    } else {
      tooltipStyle = { top: targetRect.bottom + PADDING + 12, left: leftPos, width: cardWidth };
    }
  }

  const progressPct = ((stepIndex + 1) / total) * 100;

  const renderActions = () => {
    // Branding step: two buttons - navigate to page, or skip
    if (isBranding) {
      return (
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={onNext}
            sx={{ borderRadius: 2 }}
            endIcon={<Iconify icon="carbon:arrow-right" />}
          >
            {i18n._('Upload logo and background')}
          </Button>
          <Button
            size="small"
            variant="text"
            fullWidth
            color="inherit"
            onClick={onSkip}
            sx={{ color: 'text.disabled', fontSize: 12 }}
          >
            {i18n._('Skip, I will do it later')}
          </Button>
        </Stack>
      );
    }

    if (step.interactive) {
      return (
        <Button
          variant="contained"
          fullWidth
          onClick={onNext}
          sx={{ borderRadius: 2 }}
          endIcon={<Iconify icon="carbon:arrow-right" />}
        >
          {i18n._('Continue')}
        </Button>
      );
    }

    if (isCenter) {
      return (
        <Button variant="contained" fullWidth onClick={onNext} sx={{ borderRadius: 2 }}>
          {i18n._('Continue')}
        </Button>
      );
    }

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Iconify icon="carbon:touch-1" sx={{ width: 16, height: 16, color: 'primary.main' }} />
          <Typography variant="caption" color="primary.main" fontWeight={600}>
            {i18n._('Tap the highlighted element')}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={onSkip}
          sx={{ color: 'text.disabled', fontSize: 11, minWidth: 0 }}
        >
          {i18n._('Skip')}
        </Button>
      </Stack>
    );
  };

  return (
    <Fade in timeout={250}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          zIndex: 1500,
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 360,
          width: 'calc(100vw - 32px)',
          pointerEvents: 'all',
          ...(isCenter
            ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
            : tooltipStyle),
        }}
      >
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 3,
            bgcolor: 'divider',
            '& .MuiLinearProgress-bar': { transition: 'transform 0.4s ease' },
          }}
        />
        <Stack spacing={2} sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, flex: 1 }}>
              {step.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', pt: 0.25 }}
            >
              {stepIndex + 1}/{total}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {step.body}
          </Typography>
          {renderActions()}
        </Stack>
      </Paper>
    </Fade>
  );
}

// --- Main overlay --------------------------------------------------------------

export default function TutorialOverlay() {
  const { active, currentStep, stepIndex, advance, stop } = useTutorial();
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();
  const clubIdMatch = window.location.pathname.match(/\/clubs\/([^/]+)/);
  const clubId = clubIdMatch ? clubIdMatch[1] : null;

  const total = TOTAL_STEPS;

  // Prevent accidental clicks
  const canStopRef = useRef(true);
  useEffect(() => {
    canStopRef.current = false;
    const t = setTimeout(() => {
      canStopRef.current = true;
    }, 500);
    return () => clearTimeout(t);
  }, [stepIndex]);

  const handleTapOutside = useCallback(() => {
    if (!canStopRef.current) return;
    stop();
  }, [stop]);

  const measureTarget = useCallback(() => {
    if (!currentStep?.targetId) {
      setTargetRect(null);
      return;
    }
    setTargetRect(getTargetRect(currentStep.targetId));
  }, [currentStep]);

  useEffect(() => {
    if (!active || !currentStep) return;
    if (currentStep.targetId) {
      const el = document.querySelector(`[data-tutorial-id="${currentStep.targetId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    measureTarget();
    const interval = setInterval(measureTarget, 300);
    return () => clearInterval(interval);
  }, [active, currentStep, measureTarget]);

  if (!active || !currentStep) return null;

  const handleNext = () => {
    if (currentStep.id === 'branding-upload' && clubId) {
      // Navigate to the dedicated branding upload page, tour ends there after submit
      stop();
      navigate(fillRoute(paths.clubProfile.brandingUpload, { clubId }));
    } else {
      advance();
    }
  };

  return (
    <>
      <SpotlightOverlay
        rect={targetRect}
        interactive={!!currentStep.interactive}
        onTapOutside={handleTapOutside}
      />
      <TooltipCard
        step={currentStep}
        stepIndex={stepIndex}
        total={total}
        onNext={handleNext}
        onSkip={stop}
        targetRect={targetRect}
      />
    </>
  );
}
